import React, { useEffect, useState, useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { loadDocumentVerifications } from '../actionCreators';
import { buildDMSDownloadUrl } from 'features/document/edit/documents/actionCreators';
import { showError } from 'actions/actionCreators/snackbarActions';
import { Grid } from '@mui/material';
import debounce from 'lodash.debounce';
import { RightsContext } from 'providers/RightsContext';
import { sortList } from 'utils/functions';
import DocumentVerificationComponent from './DocumentVerificationComponent';
import DocumentVerificationToolbar from './DocumentVerificationToolbar';
import DocumentVerificationSummary from './DocumentVerificationSummary';
import Modal from './ManualInterrogation/components/Modal';
import { getPagingLimits, filterManualVerificationsByValue } from '../common/functions';
import DoneAll from '@mui/icons-material/DoneAll';
import { CollapseCard, Button } from '@totalsoft/rocket-ui';
import PaginationWrapper from 'components/common/PaginationWrapper';
import CardNoResultsWrapper from 'components/layout/CardNoResultsWrapper';

const DocumentVerificationContainer = ({ documentId, siteId, isFolder }) => {
  const { readOnlyFull, clientVerificationsWrite } = useContext(RightsContext);
  const [t] = useTranslation();

  const DEBOUNCE_DELAY = 50;

  //selectors
  const loaded = useSelector(
    state =>
      state.document.verifications[`${documentId}-${siteId}`] ? state.document.verifications[`${documentId}-${siteId}`].loaded : false,
    shallowEqual
  );
  const documentVerificationList = useSelector(
    state => (state.entities.verifications ? state.entities.verifications[`${documentId}-${siteId}`] : []),
    shallowEqual
  );

  const verificationSorterOptions = [
    { id: 1, name: t('DocumentEdit.Verification.Date'), orderByProperty: 'date', descending: true },
    { id: 2, name: t('DocumentEdit.Verification.Entity'), orderByProperty: 'partnerName', descending: false },
    { id: 3, name: t('DocumentEdit.Verification.Type'), orderByProperty: 'verificationType', descending: false }
  ];

  const defaultVerificationSorterOption = {
    id: 1,
    name: t('DocumentEdit.Verification.Date'),
    orderByProperty: 'date',
    descending: true
  };

  const [currentVerificationOrder, setCurrentVerificationOrder] = useState(defaultVerificationSorterOption);

  //list
  const [documentVerifications, setDocumentVerifications] = useState(documentVerificationList);
  const [documentVerificationsOnPage, setDocumentVerificationsOnPage] = useState([]);

  const [showManualInterrogationModal, setShowManualInterrogationModal] = useState(false);

  //dispatch
  const dispatch = useDispatch();

  //functions
  const downloadFile = async fileId => {
    try {
      let link = dispatch(buildDMSDownloadUrl(fileId));
      window.location = link;
    } catch (error) {
      // continue regardless of error
    }
  };

  const showSensitiveInfoError = () => {
    dispatch(showError(t('DocumentEdit.Verification.SensitiveInfoError')));
  };

  const toggleManualInterrogationModal = () => setShowManualInterrogationModal(prevShowInterrogationModal => !prevShowInterrogationModal);

  //pagination and filtering
  const defaultConfig = useMemo(() => ({ page: 1, pageSize: 5, totalCount: 0, filter: '' }), []);
  const [paginationAndFilter, setPaginationAndFilter] = useState(defaultConfig);
  const rowsPerPageOptions = [5, 10, 20, 50];

  const getItemsPerPage = useCallback(
    (documentVerifications, pageSize, page, filter) => {
      let current = (page - 1) * pageSize;
      const sortedVerifications = sortList(
        documentVerifications,
        currentVerificationOrder.orderByProperty,
        currentVerificationOrder.descending
      );

      const documentVerificationsOnPage = sortedVerifications.slice(current, current + pageSize);
      let result;

      if (filter !== '') {
        result = filterByValue(documentVerifications, filter);
        setPaginationAndFilter({
          ...paginationAndFilter,
          totalCount: result.length,
          page: page,
          filter: filter
        });
      } else {
        result = documentVerificationsOnPage;
        setPaginationAndFilter({
          ...paginationAndFilter,
          totalCount: documentVerifications.length,
          page: page,
          filter: filter
        });
      }
      return result;
    },
    [currentVerificationOrder, paginationAndFilter]
  );

  const updatePageSize = useCallback(
    (items, paginationAndFilter) => {
      const page = paginationAndFilter.page;
      const pageSize = paginationAndFilter.pageSize;
      const filter = paginationAndFilter.filter;
      setDocumentVerificationsOnPage(getItemsPerPage(items, pageSize, page, filter));
    },
    [getItemsPerPage]
  );

  const onChangePage = useCallback(
    page => {
      updatePageSize(documentVerifications, { ...paginationAndFilter, page: page + 1 });
      setPaginationAndFilter({ ...paginationAndFilter, page: page + 1 });
    },
    [documentVerifications, paginationAndFilter, updatePageSize]
  );

  const onChangeRowsPerPage = useCallback(
    size => {
      if (documentVerifications.length < size) {
        updatePageSize(documentVerifications, { ...paginationAndFilter, pageSize: size, page: defaultConfig.page });
        setPaginationAndFilter({ ...paginationAndFilter, pageSize: size, page: defaultConfig.page });
      } else {
        updatePageSize(documentVerifications, { ...paginationAndFilter, pageSize: size });
        setPaginationAndFilter({ ...paginationAndFilter, pageSize: size });
      }
    },
    [defaultConfig.page, documentVerifications, paginationAndFilter, updatePageSize]
  );

  const onPaginationAndFiltersRefresh = useCallback(() => {
    updatePageSize(documentVerifications, {
      ...paginationAndFilter,
      pageSize: defaultConfig.pageSize,
      filter: defaultConfig.filter
    });
    setPaginationAndFilter({ ...defaultConfig, totalCount: documentVerifications.length });
  }, [updatePageSize, documentVerifications, paginationAndFilter, defaultConfig]);

  //filter changed
  const onFilterValueChangeDebounce = filter => {
    updatePageSize(documentVerifications, { ...defaultConfig, filter: filter });
  };

  const filterChangedDebounced = debounce(onFilterValueChangeDebounce, DEBOUNCE_DELAY);

  const onFilterValueChanged = (value, debounced) => {
    if (debounced) filterChangedDebounced(value);
    else {
      updatePageSize(documentVerifications, { ...defaultConfig, filter: value });
    }
  };

  const filterByValue = (verifications, filter) => {
    const result = verifications.filter(o =>
      Object.keys(o).some(k => {
        if (o[k] !== null && (k === 'comments' || k === 'verificationType' || k === 'partnerName'))
          return o[k].toString().toLowerCase().includes(filter.toString().toLowerCase());
        return null;
      })
    );
    return result;
  };

  const handleVerificationSorterChanged = verification => {
    setCurrentVerificationOrder({ ...verificationSorterOptions.find(x => x.id === verification.id) });
  };

  //effects
  useEffect(() => {
    if (!loaded) dispatch(loadDocumentVerifications(documentId, siteId));
  }, [loaded, dispatch, documentId, siteId]);

  useEffect(() => {
    setDocumentVerifications(documentVerificationList);
    if (documentVerificationList) {
      setPaginationAndFilter(prevPaginationAndFilter => ({
        ...prevPaginationAndFilter,
        totalCount: documentVerificationList.length
      }));
      setDocumentVerificationsOnPage(
        getItemsPerPage(documentVerificationList, paginationAndFilter.pageSize, paginationAndFilter.page, paginationAndFilter.filter)
      );
    }
  }, [documentVerificationList, currentVerificationOrder]);// eslint-disable-line react-hooks/exhaustive-deps

  return (
    <CollapseCard
      icon={DoneAll}
      title={t('DocumentEdit.Verification.Title')}
      subheader={<DocumentVerificationSummary verifications={documentVerifications ? documentVerifications : []} />}
      hideSubheaderOnExpand
      toggleOnHeaderClick
      content={
        <Grid container>
          <Grid item xs={12}>
            <Grid item xs={12}>
              <DocumentVerificationToolbar
                fullTextFilter={paginationAndFilter.filter}
                onFilterValueChanged={onFilterValueChanged}
                onResetFilters={onPaginationAndFiltersRefresh}
                onVerificationSorterChanged={handleVerificationSorterChanged}
                verificationSorterOptions={verificationSorterOptions}
                selectedVerificationOrder={currentVerificationOrder}
              />
            </Grid>
            <CardNoResultsWrapper content={documentVerificationsOnPage}>
              <Grid item xs={12}>
                <DocumentVerificationComponent
                  data={documentVerificationsOnPage}
                  downloadFile={downloadFile}
                  showSensitiveInfoError={showSensitiveInfoError}
                />
              </Grid>
            </CardNoResultsWrapper>
          </Grid>
          <Grid container justifyContent='space-between' alignItems='center' mt={2}>
            <Button
              id={'documentVerification_manualInterrogationBtn'}
              onClick={() => toggleManualInterrogationModal()}
              disabled={!documentVerifications || !clientVerificationsWrite || readOnlyFull}
            >
              {t('OfferEdit.Client.Verification.ManualInterrogation')}
            </Button>
            <Grid item>
              <PaginationWrapper
                loading={false}
                count={paginationAndFilter.totalCount}
                pageSize={paginationAndFilter.pageSize}
                page={paginationAndFilter.page}
                rowsPerPageOptions={rowsPerPageOptions}
                onRowsPerPageChange={onChangeRowsPerPage}
                onPageChange={onChangePage}
                onRefresh={onPaginationAndFiltersRefresh}
              />
            </Grid>
          </Grid>

          {showManualInterrogationModal && (
            <Modal
              allVerifications={documentVerifications}
              onFilterValueChanged={onFilterValueChanged}
              closePartnerManualInterrogationModal={toggleManualInterrogationModal}
              documentId={documentId}
              addNewVerifications={!isFolder}
              siteId={siteId}
              getPagingLimits={getPagingLimits}
              filterByValue={filterManualVerificationsByValue}
            />
          )}
        </Grid>
      }
    />
  );
};

DocumentVerificationContainer.defaultProps = {
  isFolder: false
};

DocumentVerificationContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  isFolder: PropTypes.bool
};

export default DocumentVerificationContainer;
